import { useEffect, useMemo, useState } from 'react';
import { ScrollView, View, Text, Image } from 'react-native';
import { Medal, Coins, Layers, Star } from 'lucide-react-native';
import { warmupHubStyles as styles } from './WarmupHub.styles';
import { GameCard } from '../GameCard/GameCard';
import { getAssetUrl } from '../../../constants/assets';
import { bannerAspectStyle } from '../../../src/quiz/helpers/gameDynamicStyles.helper';
import {
  WARMUP_BANNER_PATH,
  WARMUP_CARD_IMAGES,
  WARMUP_HUB,
  WORDLE_WIN_REWARD_COINS,
} from '../../../src/quiz/constants/warmup.constants';
import { HILO_DAILY_COIN_CAP, HILO_COINS_PER_CORRECT } from '../../../src/quiz/constants/higherLower.constants';
import { SLINGSHOT_LEVEL_COUNT, SLINGSHOT_COINS_PER_LEVEL, SLINGSHOT_CARD_ART_URI } from '../../../src/quiz/constants/slingshot.constants';
import { FOOTBALLER_WIN_REWARD_COINS } from '../../../src/quiz/constants/footballerQuiz.constants';
import { STRIKER_RUN_ACCENT, STRIKER_RUN_CARD_ART_URI, STRIKER_RUN_DAILY_COIN_CAP } from '../../../src/quiz/constants/strikerRun.constants';
import { BLOCKY_STRIKER_ACCENT, BLOCKY_STRIKER_CARD_ART_URI, BLOCKY_STRIKER_DAILY_COIN_CAP } from '../../../src/quiz/constants/blockyStriker.constants';
import { KEEPER_DUEL_DAILY_COIN_CAP, KEEPER_DUEL_CARD_ART_URI } from '../../../src/quiz/constants/keeperDuel.constants';
import { MAZE_STRIKER_ACCENT, MAZE_STRIKER_CARD_ART_URI, MAZE_STRIKER_DAILY_COIN_CAP } from '../../../src/quiz/constants/mazeStriker.constants';
import {
  MONKEY_WORLDS_ACCENT,
  MONKEY_WORLDS_CARD_ART_URI,
  MONKEY_WORLDS_DAILY_COIN_CAP,
} from '../../../src/quiz/constants/monkeyWorlds.constants';
import {
  MATCH_SPORT_ACCENT,
  MATCH_SPORT_CARD_ART_URI,
  MATCH_SPORT_DAILY_COIN_CAP,
} from '../../../src/quiz/constants/matchSport.constants';

interface WarmupHubProps {
  wordleStreak: number;
  wordleSolved: boolean;
  wordleWon: boolean;
  wordleGuessesUsed: number;
  wordleMaxGuesses: number;
  onPlayWordle: () => void;
  onPlayHilo: () => void;
  onPlaySlingshot: () => void;
  onPlayFootballer: () => void;
  onPlayStrikerRun: () => void;
  onPlayBlockyStriker: () => void;
  onPlayKeeperDuel: () => void;
  onPlayMazeStriker: () => void;
  onPlayMonkeyWorlds: () => void;
  onPlayMatchSport: () => void;
}

/** Design banner art (warmup-banner.webp) is 1694×928; used as a fallback until the real image loads. */
const BANNER_FALLBACK_RATIO = 1694 / 928;
const COUNTDOWN_TICK_MS = 30 * 1000;

function msToMidnight(now: Date) {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - now.getTime();
}

function formatCountdown(ms: number) {
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function WarmupHub({
  wordleStreak,
  wordleSolved,
  wordleWon,
  wordleGuessesUsed,
  wordleMaxGuesses,
  onPlayWordle,
  onPlayHilo,
  onPlaySlingshot,
  onPlayFootballer,
  onPlayStrikerRun,
  onPlayBlockyStriker,
  onPlayKeeperDuel,
  onPlayMazeStriker,
  onPlayMonkeyWorlds,
  onPlayMatchSport,
}: WarmupHubProps) {
  const bannerUri = useMemo(() => getAssetUrl(WARMUP_BANNER_PATH), []);
  const wordleArt = useMemo(() => getAssetUrl(WARMUP_CARD_IMAGES.wordle), []);
  const fansArt = useMemo(() => getAssetUrl(WARMUP_CARD_IMAGES.fans), []);
  const hiloArt = useMemo(() => getAssetUrl(WARMUP_CARD_IMAGES.hilo), []);
  const footballerArt = useMemo(() => getAssetUrl(WARMUP_CARD_IMAGES.footballer), []);
  const [bannerRatio, setBannerRatio] = useState(BANNER_FALLBACK_RATIO);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, []);
  const countdown = useMemo(() => formatCountdown(msToMidnight(now)), [now]);

  const wordleResult = wordleWon
    ? `Solved in ${wordleGuessesUsed}/${wordleMaxGuesses} today 🎉`
    : 'Out of guesses today';

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
      <View style={styles.bannerWrap}>
        <Image
          source={{ uri: bannerUri }}
          style={[styles.banner, bannerAspectStyle(bannerRatio)]}
          resizeMode="contain"
          accessibilityLabel="Warmup"
          onLoad={(e) => {
            // RN Web doesn't populate nativeEvent.source — keep the fallback ratio there.
            const source = e?.nativeEvent?.source;
            if (!source) return;
            const { width: w, height: h } = source;
            if (w > 0 && h > 0) setBannerRatio(w / h);
          }}
        />
      </View>

      <View style={styles.body}>
        <Text style={styles.sectionTitle}>Today&apos;s trivia</Text>

        <View style={styles.grid}>
          <GameCard
            live
            onPress={onPlayWordle}
            accent={WARMUP_HUB.wordleAccent}
            imageUri={wordleArt}
            title="Wordle"
            subtitle={wordleSolved ? wordleResult : 'Guess the mystery footballer’s surname'}
            subtitleDone={wordleSolved}
            done={wordleSolved}
            streak={wordleStreak}
            reward={WORDLE_WIN_REWARD_COINS}
            countdown={countdown}
          />

          <GameCard
            live
            onPress={onPlayHilo}
            accent={WARMUP_HUB.hiloAccent}
            imageUri={hiloArt}
            title="Higher or Lower"
            subtitle="More or fewer? Keep the streak alive"
            reward={HILO_COINS_PER_CORRECT * HILO_DAILY_COIN_CAP}
          />

          <GameCard
            live
            onPress={onPlaySlingshot}
            accent={WARMUP_HUB.slingshotAccent}
            imageUri={SLINGSHOT_CARD_ART_URI}
            title="Wall Breaker"
            subtitle="Kick the ball — topple the wall, clear the level"
            reward={SLINGSHOT_LEVEL_COUNT * SLINGSHOT_COINS_PER_LEVEL}
          />

          <GameCard
            live
            onPress={onPlayKeeperDuel}
            accent={WARMUP_HUB.keeperDuelAccent}
            imageUri={KEEPER_DUEL_CARD_ART_URI}
            title="Keeper Duel"
            subtitle="Beat the keeper — a new hazard every 3 goals"
            reward={KEEPER_DUEL_DAILY_COIN_CAP}
          />

          <GameCard
            live
            onPress={onPlayStrikerRun}
            accent={STRIKER_RUN_ACCENT}
            imageUri={STRIKER_RUN_CARD_ART_URI}
            title="Striker Run"
            subtitle="Dodge the tackles — flick past the keeper"
            reward={STRIKER_RUN_DAILY_COIN_CAP}
          />

          <GameCard
            live
            onPress={onPlayMazeStriker}
            accent={MAZE_STRIKER_ACCENT}
            imageUri={MAZE_STRIKER_CARD_ART_URI}
            title="MazeStriker"
            subtitle="Kick the ball through the maze — smash every monster"
            reward={MAZE_STRIKER_DAILY_COIN_CAP}
          />


          <GameCard

            live

            onPress={onPlayMonkeyWorlds}

            accent={MONKEY_WORLDS_ACCENT}

            imageUri={MONKEY_WORLDS_CARD_ART_URI}

            title="Monkey Worlds"

            subtitle="Run, jump and play through four sport worlds"

            reward={MONKEY_WORLDS_DAILY_COIN_CAP}

          />

          <GameCard
            live
            onPress={onPlayMatchSport}
            accent={MATCH_SPORT_ACCENT}
            imageUri={MATCH_SPORT_CARD_ART_URI}
            title="Match Sport"
            subtitle="Match 3 sports — clear the board, chase the stars"
            reward={MATCH_SPORT_DAILY_COIN_CAP}
          />

          <GameCard
            live
            onPress={onPlayBlockyStriker}
            accent={BLOCKY_STRIKER_ACCENT}
            imageUri={BLOCKY_STRIKER_CARD_ART_URI}
            title="Blocky Striker"
            subtitle="Dribble the 3D pitch — kick it in to score"
            reward={BLOCKY_STRIKER_DAILY_COIN_CAP}
          />

          <GameCard
            live
            onPress={onPlayFootballer}
            accent={WARMUP_HUB.footballerAccent}
            imageUri={footballerArt}
            title="Footballer Quiz"
            subtitle="Guess the mystery footballer in 8"
            reward={FOOTBALLER_WIN_REWARD_COINS}
          />

          <GameCard
            live={false}
            accent={WARMUP_HUB.fansAccent}
            imageUri={fansArt}
            soon
            title="Fan Favourites"
            subtitle="100 fans answered — name the top picks"
          />

          <GameCard
            live={false}
            accent={WARMUP_HUB.top10Accent}
            artGradient={WARMUP_HUB.top10CardGradient}
            icon={<Medal size={42} color={WARMUP_HUB.top10Accent} strokeWidth={1.8} />}
            soon
            title="Top 10"
            subtitle="Rank the top 10 — one slot at a time"
          />

          <GameCard
            live={false}
            accent={WARMUP_HUB.valueAccent}
            artGradient={WARMUP_HUB.valueCardGradient}
            icon={<Coins size={42} color={WARMUP_HUB.valueAccent} strokeWidth={1.8} />}
            soon
            title="Guess the Value"
            subtitle="How much is this player worth?"
          />

          <GameCard
            live={false}
            accent={WARMUP_HUB.trumpsAccent}
            artGradient={WARMUP_HUB.trumpsCardGradient}
            icon={<Layers size={42} color={WARMUP_HUB.trumpsAccent} strokeWidth={1.8} />}
            soon
            title="Pack 11"
            subtitle="Top Trumps — beat the deck"
          />

          <GameCard
            live={false}
            accent={WARMUP_HUB.buildAccent}
            artGradient={WARMUP_HUB.buildCardGradient}
            icon={<Star size={42} color={WARMUP_HUB.buildAccent} strokeWidth={1.8} />}
            soon
            title="Build a Star"
            subtitle="Draft one stat per pro — max the rating"
          />
        </View>
      </View>
    </ScrollView>
  );
}
